import type { FC, ChangeEvent, FocusEvent } from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// Dynamic t() at line 116 (studio.products.inlinePriceEdit.apply, studio.products.inlinePriceEdit.applied):
//   studio.products.inlinePriceEdit.apply
//   studio.products.inlinePriceEdit.applied
import cn from 'classnames';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import DollarIcon from '@wix/da-ds/pkg/Icons/16x16/DollarSign';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import Loader from '@wix/da-ds/pkg/Loader';
import { getIsIOS } from '@wix/da-shared-react/pkg/publicSession/selectors';

import s from './InlinePriceEdit.scss';

interface Props {
  currentValue: number;
  forceInteger?: boolean;
  onSave: (value: number, onSuccess?: () => void, onError?: () => void) => void;
}

const InlinePriceEdit: FC<Props> = ({ currentValue, forceInteger, onSave }) => {
  const { t } = useTranslation();
  const inputId = useId();
  const isIOS = useSelector(getIsIOS);
  const containerRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(currentValue);
  const [isFocused, setFocused] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [wasSaved, setWasSaved] = useState(false);
  const wasChanged = currentValue !== value;

  const handleChange = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      const parseFn = forceInteger ? parseInt : parseFloat;
      const stringValue = ev.target.value;
      setValue(stringValue ? parseFn(stringValue) : 0);
      setWasSaved(false);
    },
    [forceInteger]
  );

  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback(
    (ev: FocusEvent<HTMLInputElement>) => {
      const focusedEl = isIOS ? ev.target : ev.relatedTarget;
      if (containerRef.current?.contains(focusedEl)) {
        // if focus is moving within container don't do anything
        return;
      }
      setFocused(false);
      setWasSaved(false);
      setValue(currentValue);
    },
    [currentValue, isIOS]
  );

  const handleSave = useCallback(() => {
    if (value && value !== currentValue) {
      setSaving(true);
      onSave(
        value,
        () => {
          // on success
          setWasSaved(true);
          setSaving(false);
        },
        () => {
          // on error
          setValue(currentValue);
          setSaving(false);
        }
      );
    }
  }, [currentValue, value, onSave]);

  // update state value if price is changed from above
  useEffect(() => {
    setValue(currentValue);
  }, [currentValue]);

  const showApplyButton = Boolean(
    value && (wasChanged || wasSaved) && !isSaving
  );

  return (
    <div ref={containerRef}>
      <TextInput
        id={inputId}
        className={cn(s['root'], isFocused && s['focused'])}
        inputClassname={s['input']}
        label={t('studio.deviationList.price.label')}
        value={`${value}`}
        type="number"
        autoComplete="off"
        prefix={
          <DollarIcon
            className={s['dollar-icon']}
            size={IconSize.SMALLER}
            disableDefaultColors
          />
        }
        postfix={
          <>
            {isSaving && <Loader state="loading" size="small" />}
            {showApplyButton && (
              <PlainButtonOrLink
                className={cn(s['apply-btn'], wasSaved && s['applied'])}
                onClick={handleSave}
                disabled={wasSaved}
                type="button"
              >
                {t(
                  `studio.products.inlinePriceEdit.${wasSaved ? 'applied' : 'apply'}`
                )}
              </PlainButtonOrLink>
            )}
          </>
        }
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isLabelHidden
        noBottomSpacing
      />
    </div>
  );
};
InlinePriceEdit.displayName = 'InlinePriceEdit';

export default InlinePriceEdit;
